import { TFilterItem } from 'components/rest/Filter/FilterRow';
import RestList from 'containers/rest/List';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { Switch, Tooltip, Typography } from 'antd';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from '@redux';
import { getAddress } from '@redux/config/actions';
import { DynamicObjectType } from 'types/commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import ImportModal from 'components/rest/ImportActions/ImportModal';
import { importLocations } from '@redux/locations/actions';
import _ from 'lodash';
import { getAllApi } from 'api/crud';
import ViewStocks from '../components/ViewStocks';

const SEARCH_KEYS = ['name', 'code'];

const breadcrumbs = [
  {
    title: 'common.page.settings',
    href: '/settings',
  },
  {
    title: 'common.page.locations',
  },
];

const extraActions = [<ViewStocks />];

const initialFilter = {
  join: ['detail'],
};

export default function LocationsList() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cities = useSelector(
    (state) => state.config.address,
  ) as DynamicObjectType[];

  const importModalRef = useRef(null);
  const [dataChainStore, setDataChainStore] = useState([]);
  const resourceData = useSelector<DynamicObjectType>(
    (state) => state['locations'].data,
  );

  const filters: TFilterItem[] = useMemo(
    () => [
      {
        key: 'isStore',
        label: 'Is Store',
        dataSource: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'In Active' },
        ],
      },
      {
        key: 'isOrderProcess',
        label: 'Order processing warehouse',
        dataSource: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'In Active' },
        ],
      },
      {
        key: 'isSyncStock',
        label: 'Sync stock location',
        dataSource: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'In Active' },
        ],
      },
      {
        key: 'isActive',
        label: 'Status',
        dataSource: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'In Active' },
        ],
      },
      {
        key: 'provinceId',
        label: 'City',
        dataSource: cities?.map((item) => ({
          label: item?.name,
          value: item?.id,
        })),
        showSearch: true,
        optionFilterProp: 'label',
      },
      {
        key: 'isTabTryBuy',
        label: 'Keep & Try',
        dataSource: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'In Active' },
        ],
      },
    ],
    [cities],
  );

  const extractAddress = (provinceId) => {
    let province = '';

    if (provinceId && Array.isArray(cities) && cities?.length > 0) {
      const filterCities = cities?.filter((item) => item?.id === provinceId);

      if (Array.isArray(filterCities) && filterCities?.length > 0) {
        province = filterCities[0]?.name;
      }
    }
    return {
      province,
    };
  };

  const columns: IRestColumnProps[] = [
    {
      name: 'name',
      title: 'Name',
      render: (data: string, record) => (
        <NavLink
          to={
            record?.id ? `/settings/warehouse-locations/${record.id}/edit` : '#'
          }
        >
          <Typography.Paragraph
            ellipsis={{ rows: 2 }}
            style={{ color: '#AB2328' }}
          >
            {`${data} | ${record?.code}`}
          </Typography.Paragraph>
          {record?.isDefault && (
            <div className="location-default-text">
              {t('mores.locations.default')}
            </div>
          )}
        </NavLink>
      ),
      isDefault: true,
      width: 300,
      ellipsis: true,
    },
    {
      name: 'provinceId',
      title: 'City',
      render: (data) => <div>{extractAddress(data)?.province}</div>,
      isDefault: true,
      width: 200,
      ellipsis: true,
    },
    {
      name: 'chainStoreId',
      title: 'Brand',
      render: (data) => (
        <div>
          {dataChainStore?.filter((item) => item?.id === data)?.[0]?.name}
        </div>
      ),
      isDefault: true,
      width: 200,
      ellipsis: true,
    },
    {
      name: 'isStore',
      title: 'Is Store',
      render: (d, r, i, handleEditRow) => (
        <Switch
          onChange={(newVal) => {
            handleEditRow({ id: r.id, isStore: newVal });
          }}
          checked={!!d}
        />
      ),
      isDefault: true,
      width: 200,
      ellipsis: true,
    },
    {
      name: 'isOrderProcess',
      title: (
        <div className="d-flex align-items-center">
          <div className="mr-2.5">OPW</div>
          <Tooltip placement="top" title="Order processing warehouse">
            <FontAwesomeIcon icon={faCircleInfo} className="text-sm" />
          </Tooltip>
        </div>
      ),
      render: (d, r, i, handleEditRow) => (
        <Switch
          onChange={(newVal) => {
            handleEditRow({
              id: r.id,
              isOrderProcess: newVal,
              // isStockDistribution: newVal ? r?.isStockDistribution : false,
            });
          }}
          checked={!!d}
        />
      ),
      isDefault: true,
      width: 120,
      ellipsis: true,
    },
    {
      name: 'isSyncStock',
      title: 'Sync stock location',
      render: (d, r, i, handleEditRow) => (
        <Switch
          disabled={r?.isOrderProcess}
          onChange={(newVal) => {
            handleEditRow({ id: r.id, isSyncStock: newVal });
          }}
          checked={!!d}
        />
      ),
      isDefault: true,
      width: 200,
      ellipsis: true,
    },
    {
      name: 'isActive',
      title: 'Status',
      render: (d, r, i, handleEditRow) => (
        <Switch
          onChange={(newVal) => {
            handleEditRow({ id: r.id, isActive: newVal });
          }}
          checked={!!d}
        />
      ),
      isDefault: true,
      width: 120,
      ellipsis: true,
    },
    {
      name: 'isTabTryBuy',
      title: 'Keep & Try Store',
      render: (d, r, i, handleEditRow) => (
        <Switch
          onChange={(newVal) => {
            handleEditRow({ id: r.id, isTabTryBuy: newVal });
          }}
          checked={!!d}
        />
      ),
      isDefault: true,
      width: 200,
      ellipsis: true,
    },
  ];

  useEffect(() => {
    dispatch(getAddress());
  }, []);

  useEffect(() => {
    const allChainStoreId = _.flatMap(
      resourceData,
      (item) => item.chainStoreId,
    )?.filter((item) => item);

    // Combine all relatedIds into a single array and remove duplicates
    const uniqueChainStoreId = _.uniq(allChainStoreId);

    if (Array.isArray(uniqueChainStoreId) && uniqueChainStoreId?.length > 0) {
      const filters = { $and: [{ id: { $in: uniqueChainStoreId } }] };
      const extract = async () => {
        const data = await getAllApi('chain-stores', {
          limit: 99,
          s: JSON.stringify(filters || []),
        });
        if (Array.isArray(data?.data) || data?.data?.length > 0) {
          setDataChainStore(data?.data);
        } else {
          setDataChainStore([]);
        }
      };

      extract();
    }
  }, [resourceData]);

  return (
    <div className="location-listing">
      <RestList
        filters={filters}
        extraActions={extraActions}
        initialFilter={initialFilter}
        columns={columns}
        breadcrumbs={breadcrumbs}
        model="locations"
        rowSelectionActions={<></>}
        // formatListData={formatListData}
        searchKeys={SEARCH_KEYS}
        placeholderInputSearch={t('mores.locations.searchPlaceholder')}
        className=""
        hasImportV2={false}
        hasImport={false}
        hasExport={false}
        openModalImport={() => importModalRef.current.openModal()}
      />
      <ImportModal
        ref={importModalRef}
        source="locations"
        importActions={importLocations}
      />
    </div>
  );
}
